import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api';
import { useAdmin } from '../../context/AdminContext';

const statusOptions  = ['new', 'reviewed', 'actioned'];
const statusColors   = { new: '#3F72AF', reviewed: '#d97706', actioned: '#16a34a' };
const ratingColors   = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#84cc16', 5: '#22c55e' };
const ratingLabels   = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent' };

function Stars({ rating }) {
  return (
    <span>
      {[1,2,3,4,5].map(function(s) {
        return (
          <span key={s} style={{ color: s <= rating ? '#f59e0b' : '#d1d5db', fontSize: '1rem' }}>★</span>
        );
      })}
    </span>
  );
}

export default function AdminFeedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [selected, setSelected]   = useState(null);
  const { isSuperAdmin } = useAdmin();

  function load() {
    setLoading(true);
    var params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterRating) params.set('rating', filterRating);
    api.get('/feedback?' + params.toString())
      .then(function(res) { setFeedbacks(res.data.feedbacks); })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }

  useEffect(function() { load(); }, [filterStatus, filterRating]);

  async function updateStatus(id, status) {
    await api.put('/feedback/' + id, { status });
    load();
    if (selected && selected._id === id) {
      setSelected({ ...selected, status });
    }
  }

  async function deleteFeedback(id) {
    if (!window.confirm('Delete this feedback permanently?')) return;
    await api.delete('/feedback/' + id);
    setSelected(null);
    load();
  }

  var totalNew      = feedbacks.filter(function(f) { return f.status === 'new'; }).length;
  var avgRating     = feedbacks.length
    ? (feedbacks.reduce(function(s, f) { return s + f.rating; }, 0) / feedbacks.length).toFixed(1)
    : '—';

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0 }}>Feedback</h1>
          <p style={{ margin: '4px 0 0', opacity: 0.6, fontSize: '0.88rem' }}>
            Customer feedback submitted through the website.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total', value: feedbacks.length, icon: '💬' },
          { label: 'New', value: totalNew, icon: '🔔' },
          { label: 'Avg Rating', value: avgRating + ' / 5', icon: '⭐' }
        ].map(function(s) {
          return (
            <div key={s.label} className="card" style={{ padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span>{s.icon}</span>
                <p style={{ margin: 0, fontSize: '0.76rem', fontWeight: 700, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
              </div>
              <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--bj-clay)' }}>{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <select
          value={filterStatus}
          onChange={function(e) { setFilterStatus(e.target.value); }}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)', flex: 1, minWidth: 130 }}
        >
          <option value="">All Statuses</option>
          {statusOptions.map(function(s) { return <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>; })}
        </select>
        <select
          value={filterRating}
          onChange={function(e) { setFilterRating(e.target.value); }}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)', flex: 1, minWidth: 130 }}
        >
          <option value="">All Ratings</option>
          {[5,4,3,2,1].map(function(r) { return <option key={r} value={r}>{'★'.repeat(r)} — {ratingLabels[r]}</option>; })}
        </select>
        <button className="btn btn-outline" onClick={load} style={{ padding: '8px 14px', flexShrink: 0 }}>↻ Refresh</button>
      </div>

      {/* List */}
      {loading ? (
        <p style={{ opacity: 0.5 }}>Loading...</p>
      ) : feedbacks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', background: '#fff', borderRadius: 14, boxShadow: '0 4px 20px rgba(17,45,78,0.06)' }}>
          <p style={{ fontSize: '2rem', margin: '0 0 12px' }}>💬</p>
          <h3 style={{ margin: '0 0 8px' }}>No feedback yet</h3>
          <p style={{ opacity: 0.6, margin: 0 }}>Feedback submitted through the website will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {feedbacks.map(function(f) {
            return (
              <div
                key={f._id}
                className="card"
                style={{
                  padding: '18px 20px',
                  cursor: 'pointer',
                  borderLeft: '4px solid ' + (ratingColors[f.rating] || '#ccc'),
                  transition: 'box-shadow 0.2s'
                }}
                onClick={function() { setSelected(f); }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                      <strong style={{ fontSize: '0.95rem' }}>{f.name}</strong>
                      <span style={{ opacity: 0.5, fontSize: '0.82rem' }}>{f.email}</span>
                      <span style={{
                        background: (statusColors[f.status] || '#888') + '18',
                        color: statusColors[f.status] || '#888',
                        fontSize: '0.68rem', fontWeight: 700,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        padding: '2px 9px', borderRadius: 20
                      }}>
                        {f.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <Stars rating={f.rating} />
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: ratingColors[f.rating] }}>{ratingLabels[f.rating]}</span>
                      <span style={{ opacity: 0.45, fontSize: '0.78rem' }}>· {f.subject}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.88rem', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 500 }}>
                      {f.message}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <select
                      value={f.status}
                      onClick={function(e) { e.stopPropagation(); }}
                      onChange={function(e) { e.stopPropagation(); updateStatus(f._id, e.target.value); }}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--bj-sand)', fontFamily: 'var(--font-body)', fontSize: '0.82rem' }}
                    >
                      {statusOptions.map(function(s) { return <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>; })}
                    </select>
                    {isSuperAdmin() && (<button
                      className="btn btn-outline"
                      style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                      onClick={function(e) { e.stopPropagation(); deleteFeedback(f._id); }}
                    >
                      Delete
                    </button>)}
                  </div>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: '0.75rem', opacity: 0.4 }}>
                  {new Date(f.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
          onClick={function() { setSelected(null); }}
        >
          <div
            className="card"
            style={{ padding: 0, width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto' }}
            onClick={function(e) { e.stopPropagation(); }}
          >
            {/* Modal header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--bj-sand)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0 }}>{selected.name}</h3>
                <p style={{ margin: '3px 0 0', fontSize: '0.82rem', opacity: 0.55 }}>{selected.email} {selected.phone ? '· ' + selected.phone : ''}</p>
              </div>
              <button onClick={function() { setSelected(null); }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: 6, opacity: 0.5 }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {/* Rating + subject */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <Stars rating={selected.rating} />
                <span style={{ fontWeight: 700, color: ratingColors[selected.rating] }}>{ratingLabels[selected.rating]}</span>
                <span style={{
                  background: 'var(--bj-sand)', color: 'var(--bj-espresso)',
                  fontSize: '0.75rem', fontWeight: 600,
                  padding: '3px 10px', borderRadius: 20
                }}>
                  {selected.subject}
                </span>
              </div>

              {/* Message */}
              <div style={{
                background: 'var(--bj-cream-soft)', borderRadius: 10,
                padding: '16px 18px', marginBottom: 20,
                borderLeft: '3px solid ' + (ratingColors[selected.rating] || 'var(--bj-clay)')
              }}>
                <p style={{ margin: 0, fontSize: '0.92rem', lineHeight: 1.78, opacity: 0.85 }}>
                  {selected.message}
                </p>
              </div>

              {/* Meta */}
              <p style={{ margin: '0 0 16px', fontSize: '0.78rem', opacity: 0.45 }}>
                Submitted {new Date(selected.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>

              {/* Status update */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {statusOptions.map(function(s) {
                  var isActive = selected.status === s;
                  return (
                    <button
                      key={s}
                      onClick={function() { updateStatus(selected._id, s); }}
                      style={{
                        padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                        fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.83rem',
                        border: '1.5px solid ' + (isActive ? statusColors[s] : 'var(--bj-sand)'),
                        background: isActive ? statusColors[s] + '18' : 'transparent',
                        color: isActive ? statusColors[s] : 'var(--bj-espresso)',
                        transition: 'all 0.15s'
                      }}
                    >
                      {s[0].toUpperCase() + s.slice(1)}
                    </button>
                  );
                })}
                {isSuperAdmin() && (<button
                  onClick={function() { deleteFeedback(selected._id); }}
                  style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.83rem', border: '1.5px solid #ef4444', color: '#ef4444', background: 'transparent', marginLeft: 'auto' }}
                >
                  Delete
                </button>)}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}